/** D1 query default + reusable WHERE-clause SSOT constants. The two alias-qualified builders and the spelling screen
 *  below are each a @runtime-derived-ssot-helper emitting or reading the same soft-delete decision the consts hold, in
 *  the forms string composition cannot reach, so the spellings cannot drift apart. */

import { DB_COLUMNS } from '@federa/const/db-columns';
import { TIME_UNITS } from '@federa/const/time-units';
import { matchesAnyPattern } from '@federa/const/regex-flagless-test';
import type { NamedColumnsTable } from '@federa/types/query-conditions';

export const MESSAGES_QUERY_LIMIT = 500;
export const SOFT_DELETE_CONDITION = 'deleted = 0';
/** For nullable columns whose legacy rows may hold NULL; parenthesized for AND chains. */
export const SOFT_DELETE_CONDITION_NULL_TOLERANT = '(deleted = 0 OR deleted IS NULL)';

/** The SECOND soft-delete column spelling, on the properties / photos family. */
export const IS_DELETED_CONDITION = 'is_deleted = 0';
/** No declaring table adds NOT NULL, so legacy rows may hold NULL. */
export const IS_DELETED_CONDITION_NULL_TOLERANT = '(is_deleted = 0 OR is_deleted IS NULL)';

interface SoftDeleteAliasOptions {
	/** Required for tables whose soft-delete column is declared without NOT NULL. */
	nullTolerant?: boolean;
}

/**
 * Shared clause body for the two builders below, which differ ONLY in column spelling. NOT exported, deliberately:
 * `deleted` and `is_deleted` are DIFFERENT columns on different table families, so callers must pick a spelling by
 * NAME rather than by passing a column string a typo could get wrong. What is shared is the NULL-tolerance branching
 * -- parenthesized OR form vs bare equality -- which was duplicated, so a fix to the aliased NULL-tolerant SQL shape
 * needed editing BOTH builders.
 */
function softDeleteClauseForAlias(tableAlias: string, column: string, opts?: SoftDeleteAliasOptions): string {
	if (opts?.nullTolerant === true) {
		return `(${tableAlias}.${column} = 0 OR ${tableAlias}.${column} IS NULL)`;
	}
	return `${tableAlias}.${column} = 0`;
}

/** A BUILDER because string prefixing cannot reach the aliased NULL-tolerant form: prefixing an alias onto
 *  `SOFT_DELETE_CONDITION_NULL_TOLERANT` renders invalid SQL -- the alias must reach BOTH operands inside the parens.
 *  @runtime-derived-ssot-helper: emits the same `deleted = 0` soft-delete decision this leaf's `SOFT_DELETE_CONDITION`
 *  consts hold, in the aliased form string composition cannot reach, so the two spellings cannot drift. Layer-floor arm:
 *  22 of its consumers are `const/sql-*.ts` leaves that cannot import a `utils/` sibling. */
export function softDeleteForAlias(tableAlias: string, opts?: SoftDeleteAliasOptions): string {
	return softDeleteClauseForAlias(tableAlias, 'deleted', opts);
}

/** `is_deleted` sibling of `softDeleteForAlias`; same rationale.
 *  @runtime-derived-ssot-helper: emits the `IS_DELETED_CONDITION` decision this leaf holds for the properties/photos
 *  column spelling, aliased; same layer-floor arm as {@link softDeleteForAlias}, with eight `const/` consumers. */
export function isDeletedForAlias(tableAlias: string, opts?: SoftDeleteAliasOptions): string {
	return softDeleteClauseForAlias(tableAlias, 'is_deleted', opts);
}

/**
 * The three sanctioned SPELLINGS of a `deleted` soft-delete predicate, as a scanner recognizes them in source text:
 * the raw SQL comparison, a reference to the {@link SOFT_DELETE_CONDITION} consts above, and a call to the
 * {@link softDeleteForAlias} builder. One entry per form this leaf actually exports, which is why the recognizers are
 * homed WITH the decision: adding a fourth sanctioned spelling here without teaching the scanners about it is the
 * drift these arrays existed to prevent.
 *
 * Both soft-delete rules -- `QUAL-SQL-SOFT-DELETE-FILTER-001` (a filter is MISSING) and
 * `QUAL-SQL-SOFT-DELETE-FILTER-INVERSE-001` (a filter is present where the table has no soft-delete column) -- spelled
 * all three inline, and the inverse rule's copy carried a hand-written "keep at parity with the forward rule" note.
 * Parity by comment is what this replaces; `QUAL-NO-CROSS-TOOL-IMPORTS-001` forecloses the two rule leaves sharing
 * directly, so `const/` is the one home both may import.
 *
 * Flagless or `i`-only and used through `.test`, so the compiled instances are shared safely -- neither flag carries
 * `lastIndex`. A `/g` member would have to be exported as a source string and recompiled per consumer.
 */
const SOFT_DELETE_SPELLING_PATTERNS: readonly RegExp[] = [
	/\bSOFT_DELETE_CONDITION(?:_NULL_TOLERANT)?\b/,
	/\bdeleted\s*=\s*0\b/i,
	/\bsoftDeleteForAlias\s*\(/,
	// FOURTH sanctioned spelling, and the one exception to "one entry per form THIS leaf exports": the builder is
	// `buildActiveByIdPredicate` in `const/sql-active-by-id-d1.ts`, a same-layer const leaf whose whole body is
	// composed FROM this leaf's SOFT_DELETE_CONDITION (`id = ${placeholder} AND ${SOFT_DELETE_CONDITION}`), so it
	// cannot emit a statement without the predicate. It is enrolled HERE rather than in a per-rule list because it is
	// a SPELLING question and both soft-delete rules read this array -- registering it once keeps the forward and
	// inverse rules in agreement, which is exactly the drift this array exists to prevent: unregistered, each of its two
	// call sites is a measured false positive on the forward rule (2026-08-14).
	/\bbuildActiveByIdPredicate\s*\(/,
];

/** True when `text` carries ANY sanctioned soft-delete spelling. The whole-text screen both soft-delete rules run --
 *  as a content prefilter, and as the indeterminate-alias fallback where alias parsing cannot resolve a CTE or
 *  sub-SELECT FROM shape.
 *  @runtime-derived-ssot-helper: reads its verdict straight off this leaf's {@link SOFT_DELETE_SPELLING_PATTERNS} SSOT
 *  rather than re-spelling the three sanctioned forms, so a fourth spelling added to that array is recognized here with
 *  no edit. Layer-floor arm: both consumers are `pure/<RULE-ID>.ts` leaves that `QUAL-NO-CROSS-TOOL-IMPORTS-001` bars
 *  from sharing directly, which is the same reason the patterns array is homed here rather than in `utils/`. */
export function hasSoftDeleteSpelling(text: string): boolean {
	return matchesAnyPattern(text, SOFT_DELETE_SPELLING_PATTERNS);
}

/** True when a table schema declares the `deleted` soft-delete column -- the TABLE-side question both soft-delete
 *  rules ask before judging a query, spelled once beside the predicate spellings it pairs with.
 *  @runtime-derived-ssot-helper: same layer-floor arm as {@link hasSoftDeleteSpelling}. */
export function hasSoftDeleteColumn(table: NamedColumnsTable): boolean {
	return table.columns.some((column) => column.name === 'deleted');
}

/** Add the name of every table in ONE schema bucket that carries the {@link hasSoftDeleteColumn} column -- the bucket
 *  walk both soft-delete rules run over a binding-scoped bucket (and the inverse rule over its union fallback).
 *  @runtime-derived-ssot-helper: same layer-floor arm as {@link hasSoftDeleteSpelling}. */
export function addSoftDeleteTables(into: Set<string>, bucket: Readonly<Record<string, NamedColumnsTable>>): void {
	for (const [name, table] of Object.entries(bucket)) {
		if (hasSoftDeleteColumn(table)) into.add(name);
	}
}

export const HAS_PARENT_CONDITION = "parent_id IS NOT NULL AND parent_id != '' AND parent_id != 'null'";
export const DEFAULT_SORT_COLUMN = DB_COLUMNS.CREATED_AT;

export const BIDS_PROPERTY_ADDRESS_COLUMN = 'property_full_adress' as const;

/**
 * The house mount rule: NO refetch on mount for a read that is merely old -- it is inside its five-minute window, or
 * the focus and reconnect refetches own it -- but a refetch for one a WRITE MARKED OUT OF DATE while no screen showed it.
 *
 * THE BARE `false` THIS REPLACED DROPPED THAT MARK (operator, 2026-09-27: a deleted group stayed on the groups list until
 * a refresh). A write that settles on one view invalidates a list another view owns; with no observer mounted,
 * `invalidateQueries` only marks the query, and a flat `refetchOnMount: false` then painted the marked cache on the
 * list's next mount and never asked again. React Query takes a function here and refetches a stale query when it
 * answers true, and an invalidated query is stale by definition -- so the mark now does what the write meant by it.
 * @runtime-derived-ssot-helper (const-leaf-pinned): the house mount rule as the function React Query takes, bound by
 * the same-file `DEFAULT_QUERY_OPTIONS` so every client reads one rule.
 */
export function refetchIfInvalidated(query: { state: { isInvalidated: boolean } }): boolean {
	return query.state.isInvalidated;
}

export const DEFAULT_QUERY_OPTIONS = {
	staleTime: TIME_UNITS.MINUTE_MS * 5,
	gcTime: TIME_UNITS.MINUTE_MS * 10,
	refetchOnWindowFocus: 'always',
	refetchOnMount: refetchIfInvalidated,
	refetchOnReconnect: 'always',
} as const;

/**
 * Per-query override for a read whose freshness is owned by its OWN trigger set -- mount, cache-key change, gate open,
 * explicit refetch -- and by nothing else.
 *
 * IT EXISTS BECAUSE `DEFAULT_QUERY_OPTIONS` ANSWERS A DIFFERENT QUESTION. The house defaults describe a shared record a
 * user keeps returning to: a five-minute freshness window, no refetch on mount unless a write invalidated the read
 * ({@link refetchIfInvalidated}), and a refetch on every window focus and reconnect. An admin drill-down panel, a warehouse scan, or a photo grid carrying an in-flight local override is the
 * opposite shape -- each re-entry must show the CURRENT window, and an ambient focus refetch either re-anchors a
 * dispatch-time window the surface already paged past or repaints a grid mid-edit. Spreading this after the defaults
 * inverts exactly the four members that decide WHEN, and leaves `gcTime` alone so a re-entry still paints instantly
 * while its refetch is in flight.
 *
 * `retry: false` rides along for the same reason and is the member most easily forgotten: React Query retries a failed
 * query three times by default, so a surface converted off a hand-rolled zero-retry fetcher silently grows a ~4x
 * multiplier on every failing read unless it is spelled.
 */
export const UNCACHED_READ_QUERY_OPTIONS = {
	staleTime: 0,
	refetchOnMount: 'always',
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	retry: false,
} as const;

// QUERY_RETRY

/** How many times a failed read is retried: React Query's own default, named where a retry rule composes it rather
 *  than replacing it (`holdFailedReads`, `components/lib/api/mfe-flow-fetch-spine.ts`). */
export const QUERY_RETRY_LIMIT = 3;
